import styles from "./CMInfoEmailSubtool.module.css";
import { convertDateStringToMonthDayYearFormat } from "../../../../lib/formatting";
import Collapsible from "../../../common/Collapsible";
import { CMInfoFormInput, CMInfoSubtool } from "../../CMInfo.hooks";
import { Col, Row } from "react-grid-system";
import LabeledCheckbox from "../LabeledCheckbox";
import StyledInput from "../../../common/inputs/StyledInput";
import StyledCheckbox from "../../../common/inputs/StyledCheckbox";
import { useState } from "react";
import {
  CMInfoUpdateSectionType,
  ValidationErrors,
} from "../../../../lib/api/models";
import CMInfoSubtoolButtons from "../CMInfoSubtoolButtons";
import ValidationMessage from "../../../common/ValidationMessage/ValidationMessage";
import { EMAIL_MAX_LENGTH, partialSuccessErrorMessage } from "../../constants";

const strings = {
  email: "Email",
  primary: "Primary Email Address",
  alternate: "Alternate Email Address",
  optOut: "Opt out of Email Contact",
  lastVerifiedDate: "Last Verified Date",
  nullDate: "XX/XX/XXXX",
  clear: "Clear",
  reset: "Reset",
  update: "Update",
};

type CMInfoEmailSubtoolProps = {
  className?: string;
  isScriptOpen: boolean;
  onSetScriptOpen: (open: boolean) => void;
  onShowConfirmationModal: (
    variant: "reset" | "clear",
    onSuccess: () => void,
  ) => void;
  formState: CMInfoFormInput[CMInfoSubtool.Email];
  initialFormState: CMInfoFormInput[CMInfoSubtool.Email];
  generalDispatch: (
    payload: Partial<CMInfoFormInput[CMInfoSubtool.Email]>,
  ) => void;
  validationMessages: ValidationErrors | undefined;
  onUpdate: () => void;
  currentlyUpdating: CMInfoSubtool | "ALL" | null;
  failedSections: CMInfoUpdateSectionType[];
  startExpanded: boolean;
};

export default function CMInfoEmailSubtool({
  className,
  isScriptOpen,
  onSetScriptOpen,
  onShowConfirmationModal,
  formState,
  initialFormState,
  generalDispatch,
  validationMessages,
  onUpdate,
  currentlyUpdating,
  failedSections,
  startExpanded = false,
}: CMInfoEmailSubtoolProps) {
  const [isExpanded, setIsExpanded] = useState(
    formState.primaryEmailDueForVerification ||
      formState.alternateEmailDueForVerification ||
      formState.emailOptOutDueForVerification ||
      startExpanded,
  );
  const [primaryHasUnverifiedChanges, setPrimaryHasUnverifiedChanges] =
    useState(false);
  const [alternateHasUnverifiedChanges, setAlternateHasUnverifiedChanges] =
    useState(false);
  const [optOutHasUnverifiedChanges, setOptOutHasUnverifiedChanges] =
    useState(false);

  const isDirty =
    formState.isPrimaryEmailVerified ||
    formState.isAlternateEmailVerified ||
    formState.isEmailOptOutVerified;

  return (
    <Collapsible
      disabled={
        formState.primaryEmailDueForVerification ||
        formState.alternateEmailDueForVerification ||
        formState.emailOptOutDueForVerification ||
        isDirty
      }
      className={className}
      headerText={strings.email}
      isExpanded={isExpanded}
      onClick={() => {
        setIsExpanded(!isExpanded);
        if (isExpanded) {
          onSetScriptOpen(false);
        }
      }}
    >
      {initialFormState.emailOptOut && !isDirty ? null : (
        <>
          <Row>
            <Col md={5}>
              <StyledInput
                innerContentClassName={styles.label}
                label={strings.primary}
                value={formState.primaryEmail}
                onChange={(e) => {
                  generalDispatch({
                    primaryEmail: e.target.value,
                    isPrimaryEmailVerified: true,
                  });
                  setPrimaryHasUnverifiedChanges(true);
                }}
                validationMessages={validationMessages?.primaryEmail}
                disabled={formState.emailOptOut}
                maxLength={EMAIL_MAX_LENGTH}
              />
            </Col>
            <Col md={3}>
              <LabeledCheckbox
                className={styles.mt8}
                label={strings.lastVerifiedDate}
                checkboxLabel={convertDateStringToMonthDayYearFormat(
                  formState.primaryEmailLastVerifiedDate,
                  strings.nullDate,
                )}
                checked={formState.isPrimaryEmailVerified}
                onCheck={(isPrimaryEmailVerified) =>
                  generalDispatch({ isPrimaryEmailVerified })
                }
                isExpired={formState.primaryEmailDueForVerification}
                disabled={primaryHasUnverifiedChanges}
              />
            </Col>
          </Row>
          <Row className={styles.my10}>
            <Col md={5}>
              <StyledInput
                innerContentClassName={styles.label}
                label={strings.alternate}
                value={formState.alternateEmail}
                onChange={(e) => {
                  generalDispatch({
                    alternateEmail: e.target.value,
                    isAlternateEmailVerified: true,
                  });
                  setAlternateHasUnverifiedChanges(true);
                }}
                validationMessages={validationMessages?.alternateEmail}
                disabled={formState.emailOptOut}
                maxLength={EMAIL_MAX_LENGTH}
              />
            </Col>
            <Col md={3}>
              <LabeledCheckbox
                className={styles.mt8}
                label={strings.lastVerifiedDate}
                checkboxLabel={convertDateStringToMonthDayYearFormat(
                  formState.alternateEmailLastVerifiedDate,
                  strings.nullDate,
                )}
                checked={formState.isAlternateEmailVerified}
                onCheck={(isAlternateEmailVerified) =>
                  generalDispatch({ isAlternateEmailVerified })
                }
                isExpired={formState.alternateEmailDueForVerification}
                disabled={alternateHasUnverifiedChanges}
              />
            </Col>
          </Row>
        </>
      )}
      <Row>
        <Col md={5}>
          <StyledCheckbox
            className={styles.optOut}
            label={strings.optOut}
            checked={formState.emailOptOut}
            onChange={(e) => {
              generalDispatch({
                emailOptOut: e.currentTarget.checked,
                isEmailOptOutVerified: true,
              });
              setOptOutHasUnverifiedChanges(true);
            }}
          />
        </Col>
        <Col md={3}>
          <LabeledCheckbox
            className={styles.mt8}
            label={strings.lastVerifiedDate}
            checkboxLabel={convertDateStringToMonthDayYearFormat(
              formState.emailOptOutLastVerifiedDate,
              strings.nullDate,
            )}
            checked={formState.isEmailOptOutVerified}
            onCheck={(isEmailOptOutVerified) =>
              generalDispatch({ isEmailOptOutVerified })
            }
            isExpired={formState.emailOptOutDueForVerification}
            disabled={optOutHasUnverifiedChanges}
          />
        </Col>
      </Row>
      <Row>
        <Col className={styles.mt24}>
          <CMInfoSubtoolButtons
            isScriptOpen={isScriptOpen}
            onSetScriptOpen={onSetScriptOpen}
            onReset={() =>
              onShowConfirmationModal("reset", () => {
                generalDispatch(initialFormState);
                setPrimaryHasUnverifiedChanges(false);
                setAlternateHasUnverifiedChanges(false);
                setOptOutHasUnverifiedChanges(false);
              })
            }
            isResetDisabled={!isDirty}
            onClear={() =>
              onShowConfirmationModal("clear", () => {
                generalDispatch({
                  primaryEmail: "",
                  isPrimaryEmailVerified: true,
                  alternateEmail: "",
                  isAlternateEmailVerified: true,
                  emailOptOut: false,
                  isEmailOptOutVerified: true,
                });
                setPrimaryHasUnverifiedChanges(true);
                setAlternateHasUnverifiedChanges(true);
                setOptOutHasUnverifiedChanges(true);
              })
            }
            isClearDisabled={
              formState.primaryEmail === "" &&
              formState.alternateEmail === "" &&
              formState.emailOptOut === false &&
              (!isDirty ||
                primaryHasUnverifiedChanges ||
                alternateHasUnverifiedChanges ||
                optOutHasUnverifiedChanges)
            }
            onUpdate={onUpdate}
            isUpdateDisabled={!isDirty || !!currentlyUpdating}
            isUpdateLoading={currentlyUpdating === CMInfoSubtool.Email}
          />
        </Col>
      </Row>
      {failedSections.includes("primaryEmail") ? (
        <Row>
          <Col>
            <ValidationMessage
              validationMessages={[partialSuccessErrorMessage(strings.primary)]}
            />
          </Col>
        </Row>
      ) : null}
      {failedSections.includes("alternateEmail") ? (
        <Row>
          <Col>
            <ValidationMessage
              validationMessages={[
                partialSuccessErrorMessage(strings.alternate),
              ]}
            />
          </Col>
        </Row>
      ) : null}
      {failedSections.includes("emailOptOut") ? (
        <Row>
          <Col>
            <ValidationMessage
              validationMessages={[partialSuccessErrorMessage(strings.optOut)]}
            />
          </Col>
        </Row>
      ) : null}
    </Collapsible>
  );
}
